import type { DeliverySize } from '@packages/gql/generated/shopping/graphql';
import { Typography } from '@packages/shared';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ServiceLayer } from '@packages/modules';
import { useTracking } from '@packages/tracking';
import { useConfig } from '@packages/utilities';
import { TextWithDialog } from '../TextWithDialog';
import {
  deliveryMessages,
  deliveryMessagesDialogContent,
  deliveryMessagesDialogTitle,
} from './messages';

export type TaxHintProps = {
  /**
   * The used delivery type for a given product. Each delivery type will show its own delivery info message.
   * */
  deliverySize: DeliverySize;
};

/**
 * <p>
 * The <code>TaxHint</code> component displays a delivery info message.
 * </p>
 * */
export const TaxHint = ({ deliverySize }: TaxHintProps) => {
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();
  const {
    product: {
      taxHint: { hideAdditionalDeliveryInfo, modalContent },
    },
  } = useConfig();

  let dialogContent = <>{formatMessage(deliveryMessagesDialogContent.dialogContent)}</>;
  if (deliverySize in modalContent) {
    dialogContent = <ServiceLayer serviceLayerId={modalContent[deliverySize].link} />;
  }
  const handleClick = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: formatMessage(deliveryMessagesDialogTitle[deliverySize]).concat(
        ' (Liefertyp: ',
        deliverySize,
        ')',
      ),
    });
  };

  const messageValue = {
    link: (msg: ReactNode[]) =>
      !hideAdditionalDeliveryInfo && (
        <TextWithDialog
          message={msg}
          dialogTitle={formatMessage(deliveryMessagesDialogTitle[deliverySize])}
          dialogContent={dialogContent}
          onClickHandler={() => handleClick()}
          variant="body3"
          sx={{ color: 'text.secondary' }}
          displayTrackingProps={{ category: 'dv-service-layer-tax-hint' }}
        />
      ),
  };

  return (
    <Typography variant="body3" component="p" sx={{ color: 'text.secondary' }}>
      {formatMessage(deliveryMessages[deliverySize], messageValue)}
    </Typography>
  );
};
